/* ═══════════════════════════════════════════════════════════════
   ModuleCategoryDialogs — the four category dialogs (New / Rename /
   Move / Delete) `ModuleBrowser` opens. Split out of ModuleBrowser.tsx
   mechanically (no behavior change) — every field, message and handler
   is exactly what it was before, just moved to its own file, driven by
   the SAME `useModuleCategoryBrowser` state the browser renders from.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ConfirmDialog } from './ui/confirm-dialog';
import { SearchableSelect } from './SearchableSelect';
import type { ModuleCategoryBrowserState } from './useModuleCategoryBrowser';

export function ModuleCategoryDialogs({ state }: { state: ModuleCategoryBrowserState }) {
  const {
    newOpen,
    setNewOpen,
    newParentId,
    newName,
    setNewName,
    newBusy,
    newError,
    doNew,

    renameTarget,
    setRenameTarget,
    renameName,
    setRenameName,
    renameBusy,
    renameError,
    doRename,

    moveTarget,
    setMoveTarget,
    moveTo,
    setMoveTo,
    moveBusy,
    moveError,
    moveOptions,
    doMove,

    deleteTarget,
    setDeleteTarget,
    deleteBusy,
    deleteDirectModules,
    deleteDirectChildren,
    deleteParentLabel,
    doDelete,
  } = state;

  return (
    <>
      {/* ── New category ── */}
      <Dialog open={newOpen} onOpenChange={(o) => !newBusy && setNewOpen(o)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>New category</DialogTitle>
            <DialogDescription>
              {newParentId
                ? 'Added as a sub-category of where you are now.'
                : 'Added at the top level.'}
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
            <Label htmlFor="sf-modcat-new-name">Name</Label>
            <Input
              id="sf-modcat-new-name"
              value={newName}
              autoFocus
              aria-describedby={newError ? 'sf-modcat-new-error' : undefined}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newName.trim()) void doNew();
              }}
            />
            {newError && (
              <p
                id="sf-modcat-new-error"
                role="alert"
                aria-live="assertive"
                style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)', margin: 0 }}
              >
                {newError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)} disabled={newBusy}>
              Cancel
            </Button>
            <Button onClick={doNew} disabled={newBusy || !newName.trim()}>
              {newBusy ? 'Creating…' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Rename ── */}
      <Dialog
        open={!!renameTarget}
        onOpenChange={(o) => !renameBusy && !o && setRenameTarget(null)}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename category</DialogTitle>
          </DialogHeader>
          <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
            <Label htmlFor="sf-modcat-rename-name">Name</Label>
            <Input
              id="sf-modcat-rename-name"
              value={renameName}
              autoFocus
              aria-describedby={renameError ? 'sf-modcat-rename-error' : undefined}
              onChange={(e) => setRenameName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && renameName.trim()) void doRename();
              }}
            />
            {renameError && (
              <p
                id="sf-modcat-rename-error"
                role="alert"
                aria-live="assertive"
                style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)', margin: 0 }}
              >
                {renameError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)} disabled={renameBusy}>
              Cancel
            </Button>
            <Button onClick={doRename} disabled={renameBusy || !renameName.trim()}>
              {renameBusy ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Move to… ── */}
      <Dialog open={!!moveTarget} onOpenChange={(o) => !moveBusy && !o && setMoveTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Move "{moveTarget?.name}"</DialogTitle>
            <DialogDescription>Choose where this category lives.</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
            <Label htmlFor="sf-modcat-move-target">Move to</Label>
            <SearchableSelect
              value={moveTo}
              onChange={setMoveTo}
              options={moveOptions}
              clearable={false}
              ariaLabel="Move to"
              describedBy={moveError ? 'sf-modcat-move-error' : undefined}
            />
            {moveError && (
              <p
                id="sf-modcat-move-error"
                role="alert"
                aria-live="assertive"
                style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)', margin: 0 }}
              >
                {moveError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveTarget(null)} disabled={moveBusy}>
              Cancel
            </Button>
            <Button onClick={doMove} disabled={moveBusy}>
              {moveBusy ? 'Moving…' : 'Move'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete ── */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !deleteBusy && !o && setDeleteTarget(null)}
        title={`Delete "${deleteTarget?.name}"?`}
        description={
          <>
            Its {deleteDirectModules} module{deleteDirectModules === 1 ? '' : 's'} and{' '}
            {deleteDirectChildren} sub-categor{deleteDirectChildren === 1 ? 'y' : 'ies'} move up to
            "{deleteParentLabel}". This never deletes a module.
          </>
        }
        confirmLabel="Delete"
        busy={deleteBusy}
        onConfirm={doDelete}
      />
    </>
  );
}

export default ModuleCategoryDialogs;
